import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import * as amplitude from "@amplitude/analytics-browser";

import { useAmplitudeUserSync } from "../hooks/useAmplitudeUserSync"

import { useAnalyticsUserData } from "../context/AnalyticsUserData";
// import { useGrowthbook } from "../../context/GrowthBookContext";
import { useAppConfig } from "../../context/AppConfig/AppConfigContext";
import { useUserData } from "../../context/UserData/UserDataContext";
import { USER_ACTIONS } from "../../context/UserData/constants";
import { useEvent } from "./EventContext";
import { useEnvContext } from "../../context/EnvironmentContext";
import { useUrlContext } from "../../context/urlContext";
import { useAbTestValue } from "../../hooks/useAbTestValue";
import { isReplayPathAllowed, syncSessionReplay } from "../services/sessionReplay";
import { hasQaLogParam } from "../../utils/urlParams";

interface AmplitudeContextType {
  userAbTestVariant: string | null;
}

const AmplitudeContext = createContext<AmplitudeContextType | undefined>(undefined);

export const useAmplitude = (): AmplitudeContextType => {
  const context = useContext(AmplitudeContext);
  if (!context) {
    throw new Error("useAmplitude must be used within AmplitudeProvider");
  }
  return context;
};

interface AmplitudeProviderProps {
  children: ReactNode;
}

interface UserDataToIdentify {
  key: string;
  value: string | number | boolean | null;
}

export const AmplitudeProvider = ({ children }: AmplitudeProviderProps) => {
  const {
    abTestCampaignName,
    abTestStartCampaignDate,
    flowUseABtest,
    mosUtmConfig,
    pathPrefix,
    analyticsData: {
      amplitude_event_status: amplitudeEventConfigStatus = true,
      amplitude_session_replays: sessionReplaysConfig,
    } = {},
  } = useAppConfig();

  const { newHpath, newH2path, newSpath, newT2path, iqTestPath, learningDeskOnePath, holidayPromoPath, holidayPromo3Path, holidayPromo23dPath, w2aPath, currentFlow } = useUrlContext();

  // const { abValue } = useGrowthbook();
  const { analyticsUserData } = useAnalyticsUserData();
  const { userIdentifyEvent } = useEvent();
  const { state, dispatch } = useUserData();
  const abValue = useAbTestValue();
  const location = useLocation();
  const { isProduction } = useEnvContext();


  const prevValuesRef = useRef<Record<string, string | number | boolean | null>>({});

  const findLocalUserData = () => {
    const quizData = JSON.parse(localStorage.getItem("MemoryOSQuizData") || "{}");

    return {
      name: state.name || quizData.firstName || "",
      email: state.email || quizData.Email || "",
    };
  };

  useAmplitudeUserSync();

  // Keeps the Device ID stable across reloads instead of letting Amplitude regenerate it.
  useEffect(() => {
    if (state.device_id) {
      amplitude.setDeviceId(state.device_id);
    } else {
      const generatedDeviceId = amplitude.getDeviceId();
      if (generatedDeviceId) {
        dispatch({ type: USER_ACTIONS.SET_DEVICE_ID, payload: generatedDeviceId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.device_id]);

  useEffect(() => {
    // Session Replay runs in production (including emulation), or with ?qa_log=true.
    const isEnvironmentAllowed = isProduction || hasQaLogParam();
    const isEnabled = Boolean(sessionReplaysConfig?.enabled) && amplitudeEventConfigStatus === true;
    const allowedPaths: string[] = sessionReplaysConfig?.path ?? [];

    syncSessionReplay(
      isEnvironmentAllowed &&
        isEnabled &&
        isReplayPathAllowed(location.pathname, currentFlow, allowedPaths)
    );
  }, [
    location.pathname,
    location.search,
    currentFlow,
    sessionReplaysConfig,
    amplitudeEventConfigStatus,
    isProduction,
  ]);

  const { abTestName, abTestDate, userAbTestVariant } = useMemo(() => {
    if (learningDeskOnePath) {
      const variant = "learning_desk_one_fallback";
      return { abTestName: variant, abTestDate: variant, userAbTestVariant: variant };
    }

    if (newHpath || newH2path || newSpath || newT2path || iqTestPath || holidayPromoPath || holidayPromo3Path || holidayPromo23dPath || w2aPath) {
      if (mosUtmConfig !== null) {
        const variant = `${pathPrefix}_${mosUtmConfig}_fallback`;
        return { abTestName: variant, abTestDate: variant, userAbTestVariant: variant };
      } else if (flowUseABtest?.use_ab_test) {
        return {
          abTestName: abTestCampaignName,
          userAbTestVariant: `${pathPrefix}_variant_${abValue}`,
        };
      } else {
        return {
          abTestName: abTestCampaignName,
          userAbTestVariant: `fallback_${pathPrefix}`,
        };
      }
    } else {
      if (flowUseABtest?.useABtest) {
        return {
          abTestName: abTestCampaignName,
          userAbTestVariant: `${pathPrefix}_plan_variant_${abValue}`,
        };
      } else if (flowUseABtest?.useQuizABtest) {
        return {
          abTestName: abTestCampaignName,
          userAbTestVariant: `${pathPrefix}_quiz_variant_${abValue}`,
        };
      } else if (flowUseABtest?.useUpsellABtest) {
        return {
          abTestName: abTestCampaignName,
          userAbTestVariant: `${pathPrefix}_upsell_variant_${abValue}`,
        };
      } else {
        return {
          abTestName: abTestCampaignName,
          userAbTestVariant: `fallback_${pathPrefix}`,
        };
      }
    }
  }, [pathPrefix, flowUseABtest, abValue, newHpath, newH2path, newSpath, newT2path, iqTestPath, learningDeskOnePath, holidayPromoPath, holidayPromo3Path, holidayPromo23dPath, w2aPath, mosUtmConfig, abTestCampaignName]);

  const identifyUser = useCallback(
    (key: string, value: string | number | boolean | null) => {
      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== prevValuesRef.current[key] && amplitudeEventConfigStatus
      ) {
        userIdentifyEvent(key, value);
        prevValuesRef.current[key] = value;
      }
    },
    [userIdentifyEvent, amplitudeEventConfigStatus]
  );

  const { name, email } = findLocalUserData();

  const cleanUrl = (url: string): string => {
    return url.split("?")[0];
  };
  const path: string = cleanUrl(window.location.href);

  useEffect(() => {
    const dataToSend: UserDataToIdentify[] = [
      { key: "user_name", value: name },
      { key: "user_email", value: email },
      { key: "user_age", value: analyticsUserData.user_age },
      { key: "user_gender", value: analyticsUserData.user_gender },
      { key: "user_ab_test_number", value: userAbTestVariant },
      { key: "ab_test_name", value: abTestName },
      { key: "ab_test_start_date", value: abTestDate || abTestStartCampaignDate },
      { key: "page_url", value: path },
    ];

    dataToSend.forEach(({ key, value }) => identifyUser(key, value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    name,
    email,
    analyticsUserData,
    abTestCampaignName,
    abTestStartCampaignDate,
    userAbTestVariant,
    abTestName,
    path
  ]);

  const contextValue: AmplitudeContextType = {
    userAbTestVariant,
  };

  return (
    <AmplitudeContext.Provider value={contextValue}>
      {children}
    </AmplitudeContext.Provider>
  );
};
